import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Receipt, Upload, FileText, Loader2, X, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { pickPhotoNative, isNativePlatform } from '@/lib/nativeCamera';

const CATEGORIES = ['Fuel', 'Toll', 'Scale', 'Maintenance', 'Tire', 'Other'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV',
  'NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN',
  'TX','UT','VT','VA','WA','WV','WI','WY',
];

const categoryColors = {
  Fuel: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  Toll: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Scale: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  Maintenance: 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30',
  Tire: 'text-orange-700 dark:text-orange-400 bg-orange-500/15 dark:bg-orange-500/10 border-orange-500/40 dark:border-orange-500/30',
  Other: 'text-gray-700 dark:text-gray-400 bg-gray-500/15 dark:bg-gray-500/10 border-gray-500/40 dark:border-gray-500/30',
};

function LogExpenseModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    category: 'Fuel',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
    load_number: '',
    vendor: '',
    fuel_state: '',
    gallons: '',
    fuel_type: 'Truck',
  });
  const [receiptUrl, setReceiptUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    toast.info('Uploading receipt...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setReceiptUrl(file_url);
    setUploading(false);
    toast.success('Receipt uploaded.');
  };

  // Native app builds get a direct camera/gallery prompt instead of the
  // browser's generic file picker.
  const handleNativeReceiptCapture = async () => {
    try {
      const file = await pickPhotoNative('fuel-receipt');
      if (!file) return; // driver cancelled
      setUploading(true);
      toast.info('Uploading receipt...');
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      setReceiptUrl(file_url);
      toast.success('Receipt uploaded.');
    } catch {
      toast.error('Could not capture photo.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount.'); return; }
    if (!form.date) { toast.error('Date is required.'); return; }
    setSaving(true);
    await client.entities.Expense.create({
      driver_email: user.email,
      category: form.category,
      amount: parseFloat(form.amount),
      date: form.date,
      note: form.note || '',
      load_number: form.load_number || '',
      vendor: form.vendor || '',
      receipt_url: receiptUrl || '',
      ...(form.category === 'Fuel' && form.fuel_state ? { fuel_state: form.fuel_state } : {}),
      ...(form.category === 'Fuel' && form.gallons ? { gallons: parseFloat(form.gallons) } : {}),
      ...(form.category === 'Fuel' ? { fuel_type: form.fuel_type } : {}),
    });
    toast.success('Expense logged.');
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md z-10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-foreground">Log Expense</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Category */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Category <span className="text-red-400">*</span></label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => set('category', cat)}
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-semibold border transition-all",
                    form.category === cat ? categoryColors[cat] : 'text-muted-foreground bg-muted/40 border-border hover:border-border'
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Amount & Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Amount ($) <span className="text-red-400">*</span></label>
              <input
                type="number"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Date <span className="text-red-400">*</span></label>
              <input
                type="date"
                value={form.date}
                onChange={e => set('date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Fuel-specific fields */}
          {form.category === 'Fuel' && (
            <div className="space-y-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Fuel Details (IFTA)</p>
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Fuel Type</label>
                <div className="flex gap-2">
                  {['Truck', 'Reefer'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => set('fuel_type', t)}
                      className={cn(
                        "px-3 py-1 rounded-full text-xs font-semibold border transition-all",
                        form.fuel_type === t ? 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30' : 'text-muted-foreground bg-muted/40 border-border hover:border-border'
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">State Purchased</label>
                  <select
                    value={form.fuel_state}
                    onChange={e => set('fuel_state', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  >
                    <option value="" className="bg-background text-muted-foreground">Select state</option>
                    {US_STATES.map(s => (
                      <option key={s} value={s} className="bg-background">{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Gallons</label>
                  <input
                    type="number"
                    value={form.gallons}
                    onChange={e => set('gallons', e.target.value)}
                    placeholder="0.000"
                    step="0.001"
                    min="0"
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>
              </div>
              {form.gallons && parseFloat(form.gallons) > 0 && form.amount && parseFloat(form.amount) > 0 && (
                <div className="flex items-center justify-between text-xs px-1">
                  <span className="text-muted-foreground">Price per gallon</span>
                  <span className="font-semibold text-amber-300">
                    ${(parseFloat(form.amount) / parseFloat(form.gallons)).toFixed(3)}/gal
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Vendor + Load Number (optional) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Vendor (optional)</label>
              <input
                type="text"
                value={form.vendor}
                onChange={e => set('vendor', e.target.value)}
                placeholder="e.g. Pilot, TA, Love's"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Load # (optional)</label>
              <input
                type="text"
                value={form.load_number}
                onChange={e => set('load_number', e.target.value)}
                placeholder="Associated load"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Note (optional)</label>
            <textarea
              value={form.note}
              onChange={e => set('note', e.target.value)}
              placeholder="Description..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Receipt (optional)</label>
            <label
              onClick={(e) => {
                if (isNativePlatform()) {
                  e.preventDefault();
                  handleNativeReceiptCapture();
                }
              }}
              className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
              receiptUrl ? 'border-green-500/40 bg-green-500/5' : 'border-border bg-muted/40 hover:border-primary/40'
            )}>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleReceiptUpload} disabled={uploading} />
              {uploading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />
                : receiptUrl ? <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                : <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              <span className={cn("text-sm truncate", receiptUrl ? 'text-green-400' : 'text-muted-foreground')}>
                {uploading ? 'Uploading…' : receiptUrl ? 'Receipt uploaded — click to replace' : 'Upload receipt (PDF, JPG, PNG)'}
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border" disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.amount || !form.date} className="flex-1 bg-primary text-primary-foreground">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Log Expense'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ExpensesTab({ ytdNetEarnings = 0 }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // email -> full_name, only needed for the admin view (which lists every
  // driver's expenses together and previously showed the raw email as the
  // only way to tell whose expense a row was).
  const [driverNameByEmail, setDriverNameByEmail] = useState({});

  useEffect(() => {
    if (user?.email) loadExpenses();
  }, [user?.email]);

  useEffect(() => {
    if (!isAdmin) return;
    client.entities.DriverProfile.list('-updated_date', 500).then(profiles => {
      const map = {};
      for (const p of profiles) if (p.user_email) map[p.user_email.toLowerCase()] = p.full_name;
      setDriverNameByEmail(map);
    });
  }, [isAdmin]);

  const loadExpenses = async () => {
    setLoading(true);
    const data = isAdmin
      ? await client.entities.Expense.list('-date', 200)
      : await client.entities.Expense.filter({ driver_email: user.email }, '-date', 200);
    setExpenses(data);
    setLoading(false);
  };

  // Month summary
  const now = new Date();
  const thisMonthStr = now.toISOString().slice(0, 7); // YYYY-MM
  const monthExpenses = expenses.filter(e => (e.date || '').slice(0, 7) === thisMonthStr);
  const totalThisMonth = monthExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const netProfitThisMonth = ytdNetEarnings - totalThisMonth;

  return (
    <div className="space-y-4">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-card p-4">
          <p className="text-xs text-muted-foreground mb-1">Total Expenses This Month</p>
          <p className="text-2xl font-space font-bold text-red-400">−${totalThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-xs text-muted-foreground mt-1">{monthExpenses.length} expense{monthExpenses.length !== 1 ? 's' : ''} in {now.toLocaleString('default', { month: 'long' })}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-muted-foreground mb-1">Est. Net Profit This Month</p>
          <p className={cn("text-2xl font-space font-bold", netProfitThisMonth >= 0 ? 'text-green-400' : 'text-red-400')}>
            {netProfitThisMonth < 0 ? '-' : ''}${Math.abs(netProfitThisMonth).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">YTD net earnings minus this month's expenses</p>
        </div>
      </div>

      {/* Header + Add button */}
      <div className="flex items-center justify-between">
        <h3 className="font-space font-semibold text-foreground">My Expenses</h3>
        <Button onClick={() => setShowModal(true)} size="sm" className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-1" /> Log Expense
        </Button>
      </div>

      {/* List */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin mx-auto" />
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No expenses logged yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {expenses.map(exp => (
              <div key={exp.id} className="flex items-center justify-between px-5 py-3 hover:bg-white/3 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={cn("text-xs font-semibold px-2.5 py-0.5 rounded-full border flex-shrink-0", categoryColors[exp.category] || categoryColors.Other)}>
                    {exp.category}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{exp.note || exp.category}</p>
                    <p className="text-xs text-muted-foreground">
                      {exp.date}{exp.vendor ? ` · ${exp.vendor}` : ''}{exp.load_number ? ` · Load #${exp.load_number}` : ''}
                      {isAdmin && exp.driver_email && ` · ${driverNameByEmail[exp.driver_email.toLowerCase()] || exp.driver_email}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className="text-sm font-bold text-red-400">−${(exp.amount || 0).toFixed(2)}</span>
                  {exp.receipt_url && (
                    <a href={exp.receipt_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <LogExpenseModal
          onClose={() => setShowModal(false)}
          onSaved={loadExpenses}
        />
      )}
    </div>
  );
}